import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Package, PackageCheck, Warehouse, Tag } from "lucide-react";
import ServiceCard from "../components/ServiceCard";
import {
  STATS, SERVICES, WHO_WE_WORK_WITH, ABOUT_POINTS, FEATURES,
  PROCESS, CATEGORIES, HERO_WORDS, COMPANY,
  SELL_OPTIONS, NETWORK_POINTS,
} from "../data/mock";

function HeroRotator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_WORDS.length), 2000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="text-teal-brand transition-all duration-500">{HERO_WORDS[i]}</span>
  );
}

export default function Home() {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--brand-hero)]">
        <div className="hero-blob pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full" />
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div className="animate-fade-up">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-teal-brand shadow-sm">
              <Package size={14} /> Inventory Liquidation Specialists
            </span>
            <h1 className="text-4xl font-extrabold leading-tight text-navy-brand sm:text-5xl">
              Turn <HeroRotator /> Inventory Into Cash — Fast, and Without the Headache.
            </h1>
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-slate-500">
              We help companies protect their bottom line by taking unwanted stock off their hands.
              Overstock, slow sellers, damaged returns, discontinued lines, short-dated goods — whatever
              it is, we'll make an offer and get capital back into your business right away.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand hover:shadow-xl"
              >
                Request a Quote
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={COMPANY.phoneHref} className="text-sm font-semibold text-navy-brand hover:text-teal-brand">
                or call {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="relative animate-floaty">
            <img
              src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBpbnZlbnRvcnl8ZW58MHx8fHwxNzg3MDkxNDQ1fDA&ixlib=rb-4.1.0&q=85"
              alt="Warehouse inventory"
              className="w-full rounded-3xl object-cover shadow-2xl"
              style={{ maxHeight: 460 }}
            />
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-brand text-white">
                  <Check size={22} />
                </div>
                <div>
                  <p className="text-lg font-extrabold text-navy-brand">Paid in Full</p>
                  <p className="text-xs text-slate-500">Wire transfer on receipt</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[var(--brand-stats)]">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-5 py-14 sm:grid-cols-3 lg:px-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-4xl font-extrabold text-navy-brand sm:text-5xl" style={{ fontFamily: "Sora" }}>
                {s.value}
              </p>
              <p className="mt-2 text-sm font-medium text-slate-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-extrabold text-navy-brand sm:text-4xl">What We Do</h2>
          <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
            Once a company runs out of time, space, or outlets to sell surplus stock on its own, that's
            where we come in. In plain terms, we buy the extra. We pick up consumer goods from all kinds
            of sellers — makers, wholesalers, distributors, retailers, brands, insurers, and the
            warehouses and fulfillment centers in between.
          </p>
          <Link
            to="/services"
            className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-teal-brand px-6 py-2.5 text-sm font-semibold text-teal-brand transition-all duration-300 hover:bg-teal-brand hover:text-white"
          >
            All Services
          </Link>
        </div>

        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 3).map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </div>
        <div className="mt-8 grid gap-8 sm:grid-cols-2">
          {SERVICES.slice(3).map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </div>

        {/* Work with */}
        <div className="mt-16 overflow-hidden rounded-3xl bg-navy-brand px-6 py-12 text-white lg:px-12 lg:py-14">
          <div className="relative mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Who We Work With</span>
            <h3 className="mt-3 text-3xl font-extrabold sm:text-4xl">Trusted across the supply chain</h3>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-300">
              From the factory floor to the courtroom, sellers of every kind turn to us to convert stalled
              inventory into working capital — quickly, discreetly, and at scale.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHO_WE_WORK_WITH.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-teal-brand/40 hover:bg-white/10"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-brand/15 text-teal-brand transition-colors duration-300 group-hover:bg-teal-brand group-hover:text-white">
                    <Icon size={24} />
                  </div>
                  <h4 className="mb-1.5 text-[15px] font-bold leading-snug text-white">{w.title}</h4>
                  <p className="text-[13px] leading-relaxed text-slate-300">{w.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/submit-inventory"
              className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              Submit Your Inventory <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* AMAZON OVERSTOCK FEATURE */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="relative order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsaXF1aWRhdGlvbiUyMGJveGVzfGVufDB8fHx8MTc4NzA5MTQ0NXww&ixlib=rb-4.1.0&q=85"
              alt="Amazon overstock cartons"
              className="w-full rounded-3xl object-cover shadow-xl"
              style={{ maxHeight: 440 }}
            />
            <div className="absolute -right-4 -bottom-4 hidden rounded-2xl bg-white p-5 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-brand text-white">
                  <Warehouse size={22} />
                </div>
                <div>
                  <p className="text-lg font-extrabold text-navy-brand">Clear Your FBA</p>
                  <p className="text-xs text-slate-500">Stop long-term storage fees</p>
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-teal-brand/10 px-4 py-1.5 text-xs font-semibold text-teal-brand">
              <PackageCheck size={14} /> For Amazon &amp; Online Sellers
            </span>
            <h2 className="mt-4 text-3xl font-extrabold leading-snug text-navy-brand sm:text-4xl">
              We Buy Amazon Overstock
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Sitting on surplus Amazon-branded or white-label merchandise? We purchase overstock, aged
              FBA inventory, removal-order units, and private-label goods outright — clearing your
              fulfillment centers and turning idle stock back into cash, quickly and quietly.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { icon: Tag, t: "Amazon-branded & white-label" },
                { icon: Warehouse, t: "Aged FBA & removal orders" },
                { icon: Check, t: "Off-marketplace & discreet" },
                { icon: Check, t: "Full payment on receipt" },
              ].map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="flex items-center gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-brand/15 text-teal-brand">
                      <Icon size={15} />
                    </div>
                    <span className="text-sm font-medium text-navy-brand">{x.t}</span>
                  </div>
                );
              })}
            </div>
            <Link
              to="/amazon-overstock"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand"
            >
              Learn About Our Amazon Buying Service <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-slate-50">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:px-8">
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Team handling inventory"
              className="w-full rounded-3xl object-cover shadow-xl"
              style={{ maxHeight: 440 }}
            />
            <div className="absolute -right-4 -top-4 hidden rounded-2xl bg-teal-brand px-6 py-4 text-white shadow-lg sm:block">
              <p className="text-3xl font-extrabold" style={{ fontFamily: "Sora" }}>28</p>
              <p className="text-xs">Years of Experience</p>
            </div>
          </div>
          <div>
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">About Us</span>
            <h2 className="mt-3 text-3xl font-extrabold leading-snug text-navy-brand sm:text-4xl">
              Helping your business protect its bottom line
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              We've handled asset recovery and reverse logistics for years, and our roots run through
              manufacturing, wholesale, and retail alike. That range gives us a real feel for every link
              in the supply chain. We don't just recognize your product — we get the spot you're in and
              map out the smartest, most workable way through it.
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {ABOUT_POINTS.map((p) => (
                <div key={p} className="flex items-center gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-brand/15 text-teal-brand">
                    <Check size={15} />
                  </div>
                  <span className="text-sm font-medium text-navy-brand">{p}</span>
                </div>
              ))}
            </div>
            <Link
              to="/how-it-works"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand"
            >
              How it Works <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* TWO WAYS TO SELL */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">For Sellers</span>
          <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">Two ways to sell your inventory</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
            Every situation is different. Choose certainty and speed, or maximum recovery — either way,
            we make it simple to convert overstock into capital.
          </p>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {SELL_OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <div key={o.title} className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand text-white">
                  <Icon size={26} />
                </div>
                <h3 className="mb-3 text-xl font-bold text-navy-brand">{o.title}</h3>
                <p className="text-[15px] leading-relaxed text-slate-500">{o.text}</p>
                <Link to="/submit-inventory" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-teal-brand transition-all group-hover:gap-3">
                  Submit Inventory for Sale <ArrowRight size={16} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* SUBMIT CTA BANNER */}
      <section className="mx-auto max-w-7xl px-5 pb-4 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-navy-brand px-6 py-12 text-center text-white lg:px-12">
          <div className="hero-blob pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full" />
          <h2 className="relative text-2xl font-extrabold sm:text-3xl">Have overstock or closeouts to move?</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-sm text-slate-300">
            Send us your inventory sheet and our buyers will review it and follow up with an offer. Fast, discreet, hassle-free.
          </p>
          <Link
            to="/submit-inventory"
            className="relative mt-7 inline-flex items-center gap-2 rounded-full bg-teal-brand px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-105"
          >
            Submit Your Inventory <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="group rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-300 hover:-translate-y-2 hover:border-teal-brand/30 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand transition-colors duration-300 group-hover:bg-teal-brand group-hover:text-white">
                  <Icon size={26} />
                </div>
                <h3 className="mb-2 text-[17px] font-bold text-navy-brand">{f.title}</h3>
                <p className="text-sm leading-relaxed text-slate-500">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* NETWORK */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Our Network</span>
            <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">
              A network of buyers and sellers worldwide
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
              We link surplus stock with vetted buyers across nearly every category. For us it was never
              about a one-off sale — it's the lasting relationships that keep our clients coming out ahead.
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {NETWORK_POINTS.map((n) => {
              const Icon = n.icon;
              return (
                <div key={n.title} className="rounded-2xl bg-white p-7 shadow-sm">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand">
                    <Icon size={26} />
                  </div>
                  <h3 className="mb-2 text-[17px] font-bold text-navy-brand">{n.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500">{n.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US + PURCHASE CATEGORIES */}
      <section className="bg-[var(--brand-hero)]">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">
              Why {COMPANY.shortName}
            </span>
            <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">
              A trusted name in consumer-goods liquidation
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              For close to three decades, sellers of every size — from Fortune 500 names to independent
              wholesalers, retailers, and salvagers — have relied on us to buy their surplus. We bring the
              track record, market insight, and buyer data to quietly resell your goods through offline
              channels that never touch your main sales lanes.
            </p>
          </div>

          <div className="mt-14 rounded-3xl bg-white p-8 shadow-sm lg:p-12">
            <h3 className="text-center text-2xl font-extrabold text-navy-brand">
              What kinds of inventory do we buy?
            </h3>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-500">
              If it's a finished consumer product, chances are we'll take it. Think of just about
              everything that lines the aisles of a large retail super center.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {CATEGORIES.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-navy-brand transition-all duration-300 hover:border-teal-brand hover:bg-teal-brand hover:text-white"
                >
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-9 text-center">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-full bg-navy-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand"
              >
                Our Services <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">How it Works</span>
            <h2 className="mt-3 text-3xl font-extrabold text-navy-brand sm:text-4xl">
              Selling your inventory, made simple
            </h2>
            <p className="mt-4 text-[15px] text-slate-500">Sell your inventory in just 5 easy steps.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3 lg:grid-cols-5">
            {PROCESS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.n} className="relative rounded-2xl bg-white p-6 text-center shadow-sm transition-transform duration-300 hover:-translate-y-2">
                  <span className="absolute right-4 top-3 text-4xl font-extrabold text-slate-100" style={{ fontFamily: "Sora" }}>
                    {p.n}
                  </span>
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-brand/10 text-teal-brand">
                    <Icon size={26} />
                  </div>
                  <h3 className="mb-2 text-[15px] font-bold text-navy-brand">{p.title}</h3>
                  <p className="text-xs leading-relaxed text-slate-500">{p.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 rounded-full border-2 border-teal-brand px-6 py-2.5 text-sm font-semibold text-teal-brand transition-all duration-300 hover:bg-teal-brand hover:text-white"
            >
              See more details <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
